import { useCallback, useMemo, useRef } from "react";

/**
 * Undo/redo stacks held in refs: a snapshot, an undo or a redo mutates a stack and re-renders nothing.
 *
 * The returned object keeps one identity for the caller's lifetime, which is what lets the portfolio
 * lab hand it straight to its context `value` — anything that changed identity here would re-render
 * every lab consumer, the composition grid's per-row cell renderers included.
 */
export function useUndoRedo<T>(opts?: { maxSize?: number }) {
	const maxSize = opts?.maxSize ?? 50;
	const undoStack = useRef<T[]>([]);
	const redoStack = useRef<T[]>([]);

	const snapshot = useCallback(
		(value: T) => {
			undoStack.current.push(value);
			if (undoStack.current.length > maxSize) {
				undoStack.current.shift();
			}
			redoStack.current = [];
		},
		[maxSize],
	);

	const undo = useCallback((current: T): T | undefined => {
		if (undoStack.current.length === 0) {
			return undefined;
		}
		const entry = undoStack.current.pop()!;
		redoStack.current.push(current);
		return entry;
	}, []);

	const redo = useCallback((current: T): T | undefined => {
		if (redoStack.current.length === 0) {
			return undefined;
		}
		const entry = redoStack.current.pop()!;
		undoStack.current.push(current);
		return entry;
	}, []);

	/** Removes every undo and redo step `match` selects; the rest keep their order. */
	const discard = useCallback((match: (entry: T) => boolean) => {
		undoStack.current = undoStack.current.filter((entry) => !match(entry));
		redoStack.current = redoStack.current.filter((entry) => !match(entry));
	}, []);

	return useMemo(() => ({ snapshot, undo, redo, discard, undoStack, redoStack }), [snapshot, undo, redo, discard]);
}
